import type { BayHireSearchField, BayHireSearchFilter } from "@services/bayHireApi";
import { convertUaeToUtc, convertUtcToUae, formatDateToDDMMYYYY } from "@utils/date-time";

/**
 * DFT-0109: renders the Asia/Dubai calendar date, not the viewing browser's own local date -
 * `convertUtcToUae` shifts the UTC instant by the fixed +4 offset via pure epoch arithmetic
 * (no dependency on the browser's own timezone), and `formatDateToDDMMYYYY` formats the result
 * via string-split, also timezone-independent. Replaces the previous `new Date(value)` +
 * `date-fns` `format()` combination, which read the calendar date in the browser's own local
 * timezone - matching every other Marhub feature's browser-local convention at the time, but
 * that convention has since been overridden for Bay Hire (see `frontend-guidelines.md`).
 * `try/catch` preserves the previous fallback-to-raw-value behavior for unparseable input.
 */
export const formatBayHireDate = (value?: string | null): string => {
  if (!value) return "-";
  try {
    return formatDateToDDMMYYYY(convertUtcToUae(value));
  } catch {
    return value;
  }
};

/**
 * DFT-0044 (FE-29): `Date submitted` needs DD/MM/YYYY HH:MM, not columns.tsx's own `type:
 * "dateTime"` (US `MM/dd/yyyy`, checked against every other Marhub feature that has a
 * date+time column before writing this - none of them use that shared type either).
 * `features/vessel-call/grid/utils.ts`'s `formatDateTime` (reused by `features/licensing` for its
 * own submittedDate/expiredDate columns) is the actual established, cross-feature convention:
 * pre-format to a `dd/MM/yyyy\nHH:mm` string in the row mapper, and let the header fall through to
 * columns.tsx's plain default column (no special `type`) rather than its "dateTime" branch.
 *
 * **DFT-0109**: now renders the Asia/Dubai calendar date/time explicitly, same as
 * {@link formatBayHireDate} above - see that function's doc for why. `formatDateToDDMMYYYY`'s
 * time-included form emits exactly one space between the date and time parts
 * (`dd/MM/yyyy HH:mm`); the single `.replace(" ", "\n")` swaps it for the newline this function's
 * callers already render as two lines.
 */
export const formatBayHireDateTime = (value?: string | null): string => {
  if (!value) return "-";
  try {
    return formatDateToDDMMYYYY(convertUtcToUae(value), true).replace(" ", "\n");
  } catch {
    return value;
  }
};

/**
 * DFT-0075 (G7 bug fix, found live 2026-08-28): converts a calendar-day boundary string (no UTC
 * offset - e.g. "2026-08-19" for start-of-day, "2026-08-19T23:59:59.999" for end-of-day, both
 * produced by FiltersPanel.tsx's own formatDateOnly/formatDateOnlyEndOfDay using local Date
 * getters) into a real UTC instant, the same way useBayHireRequestForm.tsx's toISOInstant does for
 * the request's own dateFrom/dateTo at creation time.
 *
 * The bug this originally replaced: naively string-concatenating a literal "Z" onto an already-
 * local-formatted string (e.g. `${range.from}T00:00:00.000Z`) treats the local calendar date as
 * if it were also the UTC calendar date, with no actual timezone conversion.
 *
 * **DFT-0109**: the boundary string is now interpreted as an Asia/Dubai wall-clock value (via
 * `convertUaeToUtc`), not the submitting browser's own local time - the previous
 * `new Date(localDateTimeString).toISOString()` implementation had the identical browser-local
 * bug class `useBayHireRequestForm.tsx`'s `toISOInstant` had, just for the list "Date" filter
 * instead of the create form. `FiltersPanel.tsx`'s own `formatDateOnly`/`formatDateOnlyEndOfDay`
 * (shared across other Marhub modules) are untouched - this conversion stays scoped to Bay Hire's
 * own dispatch of the already-local string they produce.
 */
export const localDateBoundaryToUtcIso = (localDateTimeString: string): string =>
  convertUaeToUtc(new Date(localDateTimeString));

/**
 * DFT-0075: useBayHireRequestList.tsx's and useBayHireOperationsQueue.tsx's "dateRange" filter
 * case bodies were identical (both merge "Date" into dateFrom/dateTo via the same
 * localDateBoundaryToUtcIso conversion above) - extracted here so the two hooks share one
 * implementation instead of duplicating it.
 */
export const applyDateRangeContainmentFilter = (
  filters: BayHireSearchFilter[],
  pushFilter: (filters: BayHireSearchFilter[], field: BayHireSearchField, value: string) => void,
  range: { from?: string; to?: string },
): void => {
  if (range.from)
    pushFilter(filters, "dateFrom", localDateBoundaryToUtcIso(`${range.from}T00:00:00`));
  if (range.to) pushFilter(filters, "dateTo", localDateBoundaryToUtcIso(range.to));
};
